import styled, { css } from 'styled-components'
import Paragraph from './components/Paragraph/Paragraph'
import Icon from './components/Icon/Icon'
import Link from './components/Links/Link/Link'

export const ContentContainer = styled.div`
  max-width: 90rem;
  padding: 0 1rem;
  margin: 5rem auto;

  &:first-of-type {
    margin: 2rem auto 5rem;
  }

  @media screen and (min-width: 48rem) and (max-width: 94.5rem) {
    padding: 0 2.25rem;

    &:first-of-type {
      margin: 5rem auto 7.5rem;
    }
  }

  @media screen and (min-width: 94.5rem) {
    padding: 0;
    margin: 7.5rem auto;

    &:first-of-type {
      margin: 7.5rem auto;
    }
  }
`

export const WideImageContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  max-width: 120rem;
  padding: 0 1rem;

  @media screen and (min-width: 48rem) and (max-width: 124rem) {
    padding: 0 2.25rem;
  }

  @media screen and (min-width: 124rem) {
    padding: 0;
    margin: 0 auto;
  }

  .large {
    width: 100%;
    flex-direction: column;
    margin-bottom: 0;

    @media screen and (min-width: 64rem) {
      width: 60%;
      padding-right: 1.5rem;

      p {
        display: none;
      }
    }
  }
  .small {
    width: 40%;
    flex-direction: column;
    display: none;

    @media screen and (min-width: 64rem) {
      display: flex;
    }
  }
`

export const StyledContentContainer = styled.div`
  padding: 0;
  background-color: var(--greyDark);
  color: var(--light);
`

export const TopContent = styled.div`
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  grid-template-rows: repeat(1, auto);
  column-gap: 2%;
`

export const SidebarWrapper = styled.div`
  grid-area: auto / auto / span 4 / span 4;

  @media screen and (max-width: 48rem) {
    grid-area: auto / auto / auto / span 12;
    margin-bottom: 2.5rem;
  }
`

export const AccordionButton = styled.button`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: var(--light);
  padding: 0;

  span svg {
    fill: var(--blue);
  }
`

export const SidebarTitle = styled.h3`
  font-size: 1.5rem;
  line-height: 1.3;
  font-weight: 800;
  margin-bottom: 0.75rem;

  @media screen and (max-width: 48rem) {
    color: var(--blue);
    font-size: 1.375rem;
    margin-bottom: 0;
  }
`

export const SidebarItem = styled.li`
  margin: 0.75rem 0;

  a {
    font-size: 1.125rem;
    line-height: 1.55;
    cursor: pointer;
    color: var(--dark);
    text-decoration: none;

    &:hover {
      color: var(--blue);
    }
  }

  &.main a {
    font-weight: 700;
  }
`

export const MailToLink = styled.a<{ backgroundColor?: string }>`
  display: inline-flex;
  align-items: center;
  text-underline-offset: 0.1875rem;
  word-break: break-word;

  &:hover {
    text-decoration: none;
  }
  ${({ backgroundColor }) =>
    backgroundColor && backgroundColor === 'dark'
      ? css`
          color: var(--light);
          svg {
            fill: var(--light);
          }
        `
      : css`
          color: var(--dark);
          svg {
            fill: var(--dark);
          }
        `}
`

export const StyledLink = styled(Link)`
  &:hover {
    color: var(--blue);
    text-decoration: none;
  }
`

export const StyledLinkLarger = styled.a`
  color: var(--blue);
  font-size: 1.125rem;
  &:hover {
    text-decoration: none;
  }
`

export const StyledIcon = styled(Icon)<{ backgroundColor?: string }>`
  margin-right: 0.5rem;
`

export const MainContent = styled.section`
  grid-area: auto / 6 / auto / span 7;

  @media screen and (max-width: 48rem) {
    grid-area: auto / auto / auto / span 12;
  }
`

export const MainTitle = styled.h1`
  font-size: 2rem;
  line-height: 1.1;
  font-weight: 800;
  margin-bottom: 1.5rem;

  @media screen and (min-width: 48rem) {
    font-size: 5rem;
    line-height: 1;
    margin-bottom: 2.5rem;
  }
`

export const Title = styled.h2`
  font-size: 1.75rem;
  line-height: 1.2;
  font-weight: 800;
  margin: 2.5rem 0;
  text-align: center;

  @media screen and (min-width: 48rem) {
    font-size: 3.5rem;
    line-height: 1.1;
    margin: 5rem 0;
  }
`

export const H3Title = styled.h3`
  font-size: 1.5rem;
  line-height: 1.3;
  font-weight: 800;
  margin: 0;

  @media screen and (min-width: 48rem) {
    font-size: 2.5rem;
    line-height: 1.1;
  }
`

export const StyledH4 = styled.h4`
  font-weight: 800;
  font-size: 1.375rem;
  line-height: 1.4;

  @media screen and (min-width: 48rem) {
    font-size: 2rem;
    line-height: 1.2;
  }
`

export const StyledH5 = styled.h5`
  font-weight: 800;
  font-size: 1.125rem;
  line-height: 1.4;
  margin-bottom: 0.5rem;

  span {
    font-weight: 400;
  }

  @media screen and (min-width: 48rem) {
    font-size: 1.5rem;
    line-height: 1.3;
  }
`

export const StyledDivSmall = styled.div`
  font-weight: 400;
  font-size: 1rem;
  line-height: 1.5;
  letter-spacing: 0.0125rem;
  margin: 1rem 0;

  @media screen and (min-width: 48rem) {
    font-size: 1.125rem;
    line-height: 1.6;
  }
`

export const Quote = styled.div`
  position: relative;
  margin: 5rem 0;

  @media screen and (min-width: 48rem) {
    margin: 7.5rem 0;
  }
`

export const QuoteImage = styled.div`
  width: 100%;
  height: 0;
  max-width: 90rem;
  position: relative;
  background-image: url('/assets/images/dataPlatformServices/quoteBackground.png');
  background-repeat: no-repeat;
  background-size: contain;
  padding-top: 54%;
  display: flex;
  align-items: center;
  margin: 5rem 0;

  @media screen and (min-width: 64rem) {
    margin: 7.5rem 0;
  }
`

export const QuoteTextContainer = styled.div`
  position: relative;
  width: 100%;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  padding: 0;
  margin-top: -26%;

  @media screen and (min-width: 64rem) {
    position: absolute;
    padding: 10%;
    margin-top: 0;
  }
`

export const QuoteText = styled.blockquote`
  margin: 0;
  position: relative;
  quotes: '“' '”';
  &::before {
    content: open-quote;
    color: var(--red);
  }
  &::after {
    content: close-quote;
    color: var(--red);
  }
  font-weight: 800;
  background-color: rgba(255, 255, 255, 0.65);
  font-size: 1.5rem;
  letter-spacing: 0.0125rem;
  line-height: 1.3;
  box-shadow: 0.5rem 0.5rem 1rem 1.25rem rgba(255, 255, 255, 0.65);

  @media only screen and (min-width: 48rem) and (max-width: 75rem) {
    font-size: 2rem;
    line-height: 1.25;
  }

  @media screen and (min-width: 64rem) {
    font-size: 2.5rem;
    line-height: 1.1;
    box-shadow: 1.25rem 1.25rem 2.5rem 3.125rem rgba(255, 255, 255, 0.65);
  }
`

export const StyledRow = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  @media screen and (min-width: 64rem) {
    flex-direction: row;
  }
`

export const StyledTextBlockImageRow = styled.div<{ reverseOrder?: boolean }>`
  display: flex;
  flex-direction: column;
  margin-bottom: 0;
  scroll-snap-align: start;

  @media screen and (min-width: 48rem) {
    flex-direction: row;
    justify-content: space-between;
    width: 100%;
    margin-bottom: 5rem;

    ${({ reverseOrder }) =>
      reverseOrder &&
      css`
        flex-direction: row-reverse;
      `}
  }
`

export const SubTitle = styled(Paragraph)`
  margin: 0;
`

export const ImgWrapper = styled.div`
  margin-bottom: 1rem;
  display: flex;
  width: 100%;

  img {
    object-fit: contain;
    width: 100%;
  }
`
